import { Component, Host, h, Prop, Method, Event, EventEmitter, Element } from '@stencil/core';

/**
 * @slot - Toast message content.
 */
@Component({
  tag: 'sy-toast',
  styleUrls: ['scss/index.scss', 'scss/_tokens.scss'],
  shadow: true,
})
export class Toast {
  private isClosed = false;

  private timeoutInstance: any;

  private pauseTimeFunction: any;

  private extendTimeFunction: any;

  private typesIcons: any = {
    error: 'remove-circle',
    warning: 'alert-circle',
    success: 'check-circle-1',
    info: 'information-circle',
  };

  @Element() el: HTMLSyToastElement;

  /**
   * Type of toast. Sets the icon type and color.
   */
  @Prop({ reflect: true }) type: 'error' | 'warning' | 'success' | 'info';

  /**
   * If true, the toast can be dimissed by clicking in the close button.
   */
  @Prop({ reflect: true }) dismissable = false;

  /**
   * Defines if the toast will expire after some time.
   */
  @Prop({ reflect: true }) expirable = false;

  /**
   * Defines the time in ms that will take to the toast disappear.
   */
  @Prop({ reflect: true }) expirationTime = 3000;

  /**
   * Defines the time in ms that will take to the toast disappear if it has been hovered.
   */
  @Prop({ reflect: true }) extendedTime = 10000;

  /**
   * Emitted after toast is closed.
   */
  @Event() syToastDidClose: EventEmitter<any>;

  /**
   * Emitted after toast is expired.
   */
  @Event() syToastDidExpire: EventEmitter<any>;

  /**
   * Method responsible for closing the toast
   */
  @Method()
  async dismiss(expired?: boolean): Promise<void> {
    if (expired) {
      this.syToastDidExpire.emit();
    } else {
      this.syToastDidClose.emit();
    }
    this.el.remove();
    this.isClosed = true;
  }

  /**
   * Method responsible for pausing expiration time
   */
  @Method()
  async pauseTime(): Promise<void> {
    if (!this.isClosed && this.expirable) {
      clearTimeout(this.timeoutInstance);
    }
  }

  /**
   * Method responsible for extending the expiration time
   */
  @Method()
  async extendTime(): Promise<void> {
    if (!this.isClosed && this.expirable) {
      clearTimeout(this.timeoutInstance);
      this.timeoutInstance = setTimeout(() => this.dismiss(true), this.extendedTime);
    }
  }

  componentDidLoad() {
    if (!this.isClosed && this.expirable) {
      this.timeoutInstance = setTimeout(() => this.dismiss(true), this.expirationTime);

      this.extendTimeFunction = this.extendTime.bind(this);
      this.pauseTimeFunction = this.pauseTime.bind(this);

      this.el.addEventListener('mouseleave', this.extendTimeFunction);
      this.el.addEventListener('mouseenter', this.pauseTimeFunction);
    }
  }

  disconnectedCallback() {
    if (this.el.isConnected) return;

    clearTimeout(this.timeoutInstance);

    this.el.removeEventListener('mouseleave', this.extendTimeFunction);
    this.el.removeEventListener('mouseenter', this.pauseTimeFunction);
  }

  render() {
    return (
      <Host role="alert" aria-live="assertive" aria-atomic="true">
        <div class="sy-toast-wrapper">
          <sy-icon class="sy-toast-icon" name={this.typesIcons[this.type]}></sy-icon>
          <div class="sy-toast-content">
            <slot></slot>
          </div>
        </div>
        {this.dismissable && <sy-dismiss class="sy-toast-dismiss" onClick={() => this.dismiss()}></sy-dismiss>}
      </Host>
    );
  }
}
